import { get } from 'svelte/store';
import { bulkUpdate } from 'src/components/DevicesSettings/ApplicationSettings/app_settings_helper';
import { control_bluetooth_device_android } from 'src/services/native_container_requests_android';
import { control_bluetooth_device_ios } from 'src/services/native_container_requests_ios';
import { devices } from 'src/stores/lists/devices';
import { controlBleDevice } from 'src/stores/features/ble/control-device';
import { app, os } from 'src/stores/session/native-bridge';
import { BLU_STATUS } from 'src/types/requests/settings/blu_status';
import { real_id } from './interface_helpers';
import { isBleSettingsSessionReady } from './ble_settings_session_ready';

export { BLE_SETTINGS_CONNECTED_STATE, isBleSettingsSessionReady } from './ble_settings_session_ready';

/**
 * In-flight native connect request. `updateDeviceId` / `updateProcessRunning`
 * notify `$controlBleDevice` without changing `currentStatusIndex`, so the
 * reactive resume call would otherwise re-issue `connect` until native
 * reports a different index.
 */
let pendingConnect: { deviceId: string; statusIndex: number | null } | null = null;

/**
 * Clears the in-flight connect guard. Safe to call after `controlBleDevice.reset()`.
 */
export function clearBleSettingsConnectRequest(): void {
	pendingConnect = null;
}

/**
 * Ask native to connect this BLE device, unless the same restart status
 * already has a connect in flight.
 * @param deviceId - Device to connect
 * @param triggeringStatusIndex - `currentStatusIndex` that decided we should connect
 */
export function startBleSettingsConnection(
	deviceId: string,
	triggeringStatusIndex?: number | null,
): void {
	if (!get(app) || !deviceId) return;

	const statusIndex =
		triggeringStatusIndex !== undefined
			? triggeringStatusIndex
			: get(controlBleDevice).currentStatusIndex;
	const { processRunning, deviceId: storeDeviceId } = get(controlBleDevice);
	const requestStillInStore = processRunning || storeDeviceId === deviceId;
	const alreadyRequested =
		pendingConnect?.deviceId === deviceId &&
		pendingConnect.statusIndex === statusIndex &&
		requestStillInStore;

	if (alreadyRequested) return;

	pendingConnect = { deviceId, statusIndex };
	controlBleDevice.updateDeviceId(deviceId);
	controlBleDevice.updateProcessRunning(true);
	if (get(os) === 'IOS') {
		control_bluetooth_device_ios(deviceId, 'connect');
	} else {
		control_bluetooth_device_android(deviceId, 'connect');
	}
}

/**
 * Resume the connect flow when the session is idle or was dropped.
 * No-ops while already connected, while waiting for a button press, or
 * while a connect for the same restart status is already in flight.
 * @param deviceId - Device whose settings session should be live
 * @param bleState - Current BLE control-store snapshot
 */
export function resumeBleSettingsConnectionIfNeeded(
	deviceId: string,
	bleState: { currentStatusIndex: number | null; connectionState: number },
): void {
	if (!deviceId || isBleSettingsSessionReady(bleState.connectionState)) {
		if (pendingConnect?.deviceId === deviceId) pendingConnect = null;
		return;
	}

	const { currentStatusIndex, connectionState } = bleState;
	const shouldRestart =
		currentStatusIndex === null ||
		currentStatusIndex === BLU_STATUS.DISCONNECT_FROM_DEVICE ||
		(currentStatusIndex === BLU_STATUS.UNHANDLED_STATE &&
			connectionState === BLU_STATUS.BUTTON_PRESS_REQUIRED);

	if (!shouldRestart) {
		if (pendingConnect?.deviceId === deviceId) pendingConnect = null;
		return;
	}

	startBleSettingsConnection(deviceId, currentStatusIndex);
}

export function readStoredBleDevice<T extends { id?: string }>(device: T): T {
	if (!device?.id) return device;
	return (get(devices)[device.id] ?? get(devices)[real_id(device.id)] ?? device) as T;
}

/** Write a BLE setting to the local device immediately, then persist via bulk update. */
export function persistBleDeviceSetting(device: { id?: string }, patch: Record<string, unknown>): void {
	const id = device?.id;
	if (!id) return;

	const live = readStoredBleDevice(device);
	devices.update({ ...live, ...patch } as typeof live);
	bulkUpdate(id, patch);
}
